import React, { CSSProperties } from 'react';

export const UNITS = {
  X: {
    FORTY_FIVE: 71,
    THIRTEEN: 20.5,
    TWO: 10,
    VOCAB_TOP: 3.69,
  },
  Y: {
    CIRCLE_OFFSET: 20,
    THREE: 5,
  },
};

const getBackgroundSize = (fit) => {
  switch (fit) {
    case 'stretch':
      return '100% 100%';
    default:
      return fit;
  }
};

export const backgroundStyle = (background) => {
  const backgroundImage = background && background.url
    ? `url(${background.url.replaceAll("'", "\\'")})`
    : '';

  const backgroundPosition = background && background.position;
  const setBackgroundSize = background && background.size;

  if (!backgroundPosition && !setBackgroundSize) {
    return { backgroundImage };
  }

  const [backgroundPositionX, positionY] = backgroundPosition.split(' ');
  const isCustom = positionY.match(/\d+/g);
  const backgroundPositionY = `${positionY}${isCustom ? '%' : ''}`;

  const backgroundSize = setBackgroundSize ? `${getBackgroundSize(background.size)}` : '';

  return ({
    backgroundImage,
    backgroundPositionX,
    backgroundPositionY,
    backgroundSize,
  });
};

export const positionedContainerStyle = (left: number, top: number, width: number, height: number) => ({
  opacity: 1,
  position: 'absolute',
  left: `${left}%`,
  top: `${top}%`,
  width: `${width}%`,
  height: `${height}%`,
} as CSSProperties);

// TODO: this will be updated later when styling reqs are updated
export const sizedContainerStyle = (hasChildren: boolean = false) => ({
  position: 'relative',
  userSelect: 'auto',
  width: '100%',
  height: hasChildren ? 'auto' : '100%',
  boxSizing: 'border-box',
} as CSSProperties);

export const resizeWithinContainer = (elementRef, containerWidth, containerHeight) => {
  if (!elementRef || !elementRef.current || !containerHeight || !containerWidth) return;

  const height = elementRef.current.clientHeight;
  const scaleFactor = height > containerHeight ? containerHeight / height : 1;
  const inverseScaleFactor = 1 / scaleFactor;

  $(elementRef.current).css({ transform: `scale(${scaleFactor})` });

  const draggableButton = elementRef.current.querySelector('[data-draggable-location="dropzone"]');
  if (draggableButton) {
    $(draggableButton).css({ transform: `scale(${inverseScaleFactor})`, transformOrigin: 'top right' });
  }

  const scoreIcon = elementRef.current.querySelector('[data-score-icon="true"]');
  if (scoreIcon) {
    $(scoreIcon).css({ transform: `scale(${inverseScaleFactor})` });
  }
};

type Term = { definition: string, id: number, kind: string, s3_url?: string, term: string };

export const formatVocab = (vocabTerms: Term[]) => vocabTerms.map(term => ({
  definition: term.definition,
  image: term.s3_url,
  kind: term.kind,
  label: <div><strong>{term.term}:</strong> <span dangerouslySetInnerHTML={{ __html: term.definition }} /></div>,
  term: term.term,
  value: term.id,
  vocabDefinition: <div>{term.definition}</div>,
  vocabTerm: <div>{term.term}</div>,
}));

export const getTextContent = (vocabId, vocabularyTerms, vocabLoading, text, valueToReturn) => {
  if (vocabId && !vocabLoading) {
    const vocabTerm = vocabularyTerms.find(vocab => vocab.value === vocabId);

    if (!vocabTerm) return 'Undefined Vocabulary Term';

    switch (valueToReturn) {
      case 'term':
        return vocabTerm.term;
      case 'images':
        return vocabTerm.image;
      case 'definition':
      default:
        return vocabTerm.definition;
    }
  }

  return text;
};

export const getTextAreaNode = (id: string) => {
  const label = `Text area ${id.toUpperCase()}`;
  return document.querySelector(`[aria-label="${label}"]`);
};

export const getDimensions = (shape: string, height: number, width: number) => {
  // Will return the same height and width for when the dropzone shape is circular to create circles instead of ovals
  if (shape === 'circle') {
    return {
      // To get a perfect circle, we have to use the ratio of our builder's width to height, 640/540 = 1.185.
      height: width * 1.185,
      width,
    };
  }

  // Regular dimensions for rectangles since they aren't limited to a specific shape.
  return {
    height,
    width,
  };
};

// Returns sizes for image text areas based on the number of text areas.
export const getTextAreaDimensions = (numberOfTextAreas, linkOption, currentWidth, currentHeight) => {
  if (linkOption === 'definition') {
    return { height: currentHeight, width: currentWidth };
  }
  if (numberOfTextAreas === 1) {
    return { height: 60, width: 70 };
  }
  if (numberOfTextAreas === 2 || numberOfTextAreas === 4) {
    return { height: 40, width: 45 };
  }
  return { height: 35, width: 30 };
};

// Coordinate positions for image text areas.
export const IMAGE_TEXT_AREA_POSITIONS = [
  // One DnD Option
  { POSITIONS: [{ left: 5, top: 10 }] },
  // Two DnD Options
  { POSITIONS: [
    { left: 5, top: 10 },
    { left: 50, top: 10 },
  ] },
  // Three DnD Options
  { POSITIONS: [
    { left: 5, top: 10 },
    { left: 36, top: 10 },
    { left: 67, top: 10 },
  ] },
  // Four DnD Options
  { POSITIONS: [
    // First Row
    { left: 5, top: 10 },
    { left: 50, top: 10 },
    // Second Row
    { left: 5, top: 53 },
    { left: 50, top: 53 },
  ] },
  // Five DnD Options
  { POSITIONS: [
    // First Row
    { left: 5, top: 10 },
    { left: 36, top: 10 },
    { left: 67, top: 10 },
    // Second Row
    { left: 15, top: 48 },
    { left: 47, top: 48 },
  ] },
  // Six DnD Options
  { POSITIONS: [
    // First Row
    { left: 5, top: 10 },
    { left: 36, top: 10 },
    { left: 67, top: 10 },
    // Second Row
    { left: 5, top: 48 },
    { left: 36, top: 48 },
    { left: 67, top: 48 },
  ] },
];

// Constants to position drop zones based on the number of text areas.
export const DROP_ZONE_POSITION_CONSTANTS = [
  { x: 2.3, y: 2 },
  { x: 1.6, y: 2 },
  { x: 1.2, y: 1.8 },
  { x: 1.6, y: 2 },
  { x: 1.2, y: 1.8 },
  { x: 1.2, y: 1.8 },
];

// Reads the rendered height of each text area as a percentage of the drop-zone container.
// This is the only place vocab spacing needs to touch the DOM — call it from a layout effect
// once the boxes (and their content) have committed, then feed the result to computeSpacedTextAreas.
export const measureHeightsPct = (ids: string[]): { [id: string]: number } => {
  const container = document.querySelector('section[aria-label="Drop Zones"]');
  if (!container) return {};

  const containerHeight = container.clientHeight || 1;

  return ids.reduce((acc, id) => {
    const node = getTextAreaNode(id);
    acc[id] = node ? (node.getBoundingClientRect().height / containerHeight) * 100 : 0;
    return acc;
  }, {} as { [id: string]: number });
};

// Pure vertical layout for vocab text areas. Stacks each box below the previous one using its
// measured height (in % of the container), clamping every box inside the container so nothing
// renders outside the `overflow: hidden` arena. The first box keeps its existing top.
export const computeSpacedTextAreas = (
  textAreas: { [id: string]: any },
  heightsPct: { [id: string]: number },
  { shape, linkOption }: { shape: string; linkOption: string },
): { [id: string]: any } => {
  const ids = Object.keys(textAreas);
  if (ids.length === 0) return {};

  if (linkOption === 'images') {
    const imagePositions = IMAGE_TEXT_AREA_POSITIONS[ids.length - 1];
    if (!imagePositions) return {};

    return ids.reduce((acc, id, i) => {
      const position = imagePositions.POSITIONS[i];
      if (position) acc[id] = { ...textAreas[id], ...position };
      return acc;
    }, {} as { [id: string]: any });
  }

  const offset = shape === 'rectangle' ? UNITS.Y.THREE : UNITS.Y.CIRCLE_OFFSET;
  const clamp = (top: number, height: number) => Math.max(0, Math.min(top, 100 - height));

  const next: { [id: string]: any } = { [ids[0]]: textAreas[ids[0]] };
  let prevTop = textAreas[ids[0]].top;

  for (let i = 1; i < ids.length; i += 1) {
    const id = ids[i];
    const prevHeight = heightsPct[ids[i - 1]] || 0;
    const top = clamp(Math.round(prevTop + prevHeight + offset), heightsPct[id] || 0);

    next[id] = { ...textAreas[id], top };
    prevTop = top;
  }

  return next;
};

// Pure dropzone alignment. Positions each dropzone relative to its matching text area (same key),
// reading the text area's coordinates from state — the previous implementation read these back out
// of the DOM via `textarea.style.*`, which is why it had to run after a commit.
export const matchDropzonesToTextAreas = (
  dropZones: { [id: string]: any },
  textAreas: { [id: string]: any },
  linkOption: string,
): { [id: string]: any } => {
  const dropZoneConstants = DROP_ZONE_POSITION_CONSTANTS[Object.keys(dropZones).length - 1];

  return Object.entries(dropZones).reduce((acc, [key, dropZone]: [string, any]) => {
    const textArea = textAreas[key];
    if (!textArea) return acc;

    if (linkOption === 'images') {
      if (!dropZoneConstants) return acc;

      acc[key] = {
        ...dropZone,
        left: textArea.left + textArea.width - (dropZoneConstants.x * dropZone.width),
        top: textArea.top + textArea.height - (dropZoneConstants.y * dropZone.height),
      };
      return acc;
    }

    acc[key] = { ...dropZone, top: textArea.top - (dropZone.height / 2) };
    return acc;
  }, {} as { [id: string]: any });
};

export const isVocabQuestion = (choices) => {
  if (!choices) return false;

  // @ts-ignore
  return Object.values(choices).some(choice => choice.type === 'vocab');
};
